import React from 'react';
import { motion } from 'framer-motion';
import { MaskText, Reveal, SectionLabel } from './ui/Reveal';
import { MagneticButton } from './ui/MagneticButton';
import { featuredProject } from '../data/site';
import { usePrefersReducedMotion } from '../hooks/useMedia';
type Props = {
  /** Show the three-image gallery strip. Off for the homepage preview. */
  withGallery?: boolean;
};

/** Editorial case-study preview of the real featured identity project. */
export function FeaturedProject({
  withGallery = true
}: Props) {
  const reduced = usePrefersReducedMotion();
  const project = featuredProject;
  return <section className="bg-surface2 py-20 sm:py-28" aria-label="Featured project" data-id="element-198" data-mp="2juftkh0r3qcn">
      <div className="mx-auto w-full max-w-container px-5 sm:px-8" data-id="element-199" data-mp="2os7shuztccqc">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-200" data-mp="2ouk8i2m0gd0r">
          <div data-id="element-201" data-mp="18gkdn620vdkd">
            <SectionLabel data-id="element-202" data-mp="1ddkgz97hm27s">Featured Identity Project</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-4xl font-extrabold uppercase leading-[0.92] tracking-tightest sm:text-6xl" data-id="element-203" data-mp="1devljb8rbdzy">
              <MaskText text={project.title} by="word" data-id="element-204" data-mp="c28v0fdiekh4" />
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-3" data-id="element-205" data-mp="c4ljzchopv3o">
            {project.categories.map(category => <span key={category} className="label border border-line px-3 py-1.5 text-muted" data-id="element-206" data-mp="m0oi879f19lc">
                {category}
              </span>)}
            <span className="label text-muted" data-id="element-207" data-mp="zyd6zq6lt0vo">{project.year}</span>
          </div>
        </div>

        <Reveal className="mt-10 grid gap-8 lg:grid-cols-[1fr_0.85fr] lg:items-end" data-id="element-208" data-mp="v0tgnrwmsxnd">
          <div className="relative overflow-hidden" data-id="element-209" data-mp="19oocvi9ulk57">
            <motion.img src={project.cover} alt="Apparel identity applied to an embroidered cap and chest print" className="w-full object-cover" initial={reduced ? undefined : {
            scale: 1.08
          }} whileInView={{
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.4,
            ease: [0.16, 1, 0.3, 1]
          }} loading="lazy" decoding="async" data-cursor="label" data-cursor-label="View project" data-id="element-210" data-mp="14r6bqe22o97r" />
          </div>
          <div data-id="element-211" data-mp="1vodikqmduat1">
            <p className="max-w-lg text-base leading-relaxed text-muted sm:text-lg" data-id="element-212" data-mp="20h6k75mk130w">
              {project.intro}
            </p>
            <ul className="mt-7 space-y-0 border-t border-line" data-id="element-213" data-mp="24jjodplnksvk">
              {project.scope.map(item => <li key={item} className="flex items-center justify-between border-b border-line py-3 text-sm" data-id="element-214" data-mp="29gtktp7gm4sw">
                  <span data-id="element-215" data-mp="2jfr8kpx29uau">{item}</span>
                  <span className="label text-brand" data-id="element-216" data-mp="2jepz2pvp1fv8">Delivered</span>
                </li>)}
            </ul>
            <div className="mt-8" data-id="element-217" data-mp="k4ribu98feg9">
              <MagneticButton to="/work" variant="outline" cursorLabel="Explore" showArrow data-id="element-218" data-mp="p1qba9h5tfcv">
                Explore Our Work
              </MagneticButton>
            </div>
          </div>
        </Reveal>

        {withGallery ? <div className="mt-6 grid gap-6 md:grid-cols-3" data-id="element-219" data-mp="2xki2zkcefa54">
            {project.gallery.slice(0, 3).map((item, index) => <Reveal key={item.src} delay={index * 0.08} data-id="element-220" data-mp="2tk7p7lid7ffp" data-mpi={index}>
                <figure className="group" data-id="element-221" data-mp="2olo23hs1bxlr">
                  <div className="overflow-hidden bg-white" data-id="element-222" data-mp="2omplc9j7fqek">
                    <img src={item.src} alt={item.caption} className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" loading="lazy" decoding="async" data-id="element-223" data-mp="3la42k8wonrdn" />
                  </div>
                  <figcaption className="label mt-3 text-muted" data-id="element-224" data-mp="37dqkaxkbrly4">
                    0{index + 1} — {item.caption}
                  </figcaption>
                </figure>
              </Reveal>)}
          </div> : null}
      </div>
    </section>;
}